import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { CheckCircle2, AlertTriangle, Save, Play, ArrowLeft } from "lucide-react";

const SECTIONS = [
  ["plant_capacity", "Plant Capacity Semantics"],
  ["finance", "Finance Cost"],
  ["rm_requirement", "RM Reverse Calculation"],
  ["packing_material_stock", "Packing Material Stock"],
  ["consumable_costs", "Consumable Costs"],
  ["product_aliases", "Product Aliases"],
  ["buyer_brand_map", "Buyer / Brand Mapping"],
  ["documentation", "Documentation Status"],
];

const TESTS = [
  ["2-3 Years Capacity", "/ai/ceo-chat/capacity-comparison", {}],
  ["Documentation Status", "/ai/ceo-chat/documentation-status", {}],
  ["Finance Cost", "/ai/ceo-chat/finance-cost-approved", {}],
  ["Sysco Packing Material", "/ai/ceo-chat/packing-material-availability-approved", { preset: "sysco" }],
  ["Plant Consumable Expense", "/ai/ceo-chat/consumables-expense-approved", {}],
  ["Finished Product + Breaded", "/ai/ceo-chat/finished-product-volume-approved", {}],
  ["RM Reverse Calculation", "/ai/ceo-chat/rm-requirement-approved", { count: 65 }],
];

function JsonEditor({ title, value, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [err, setErr] = useState("");
  useEffect(() => setText(JSON.stringify(value ?? {}, null, 2)), [value]);
  const apply = (next) => {
    setText(next);
    try {
      const parsed = JSON.parse(next);
      setErr("");
      onChange(parsed);
    } catch (e) {
      setErr("JSON is not valid yet");
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {err ? <span className="text-xs text-red-600">{err}</span> : <span className="text-xs text-slate-400">Approved inputs only</span>}
      </div>
      <textarea
        value={text}
        onChange={(e) => apply(e.target.value)}
        spellCheck={false}
        className="min-h-[220px] w-full rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-5 text-slate-800 outline-none focus:border-blue-400"
      />
    </div>
  );
}

export default function ClientRequirementsCompletion() {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/ceo/client-requirements");
        if (!alive) return;
        setRequirements(r.data?.requirements || []);
        setInputs(r.data?.business_inputs || {});
      } catch (e) {
        if (alive) setMessage(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const approved = useMemo(() => {
    return SECTIONS.reduce((n, [key]) => n + (inputs?.[key]?.approved ? 1 : 0), 0);
  }, [inputs]);

  const save = async () => {
    setSaving(true); setMessage("");
    try {
      const r = await api.put("/ceo/business-inputs", inputs);
      setInputs(r.data?.data || inputs);
      setMessage("Business inputs saved. Ask AI and the completion endpoints will use approved values immediately.");
    } catch (e) {
      setMessage(formatErr(e));
    } finally { setSaving(false); }
  };

  const runTest = async ([label, path, payload]) => {
    setTestLoading(label); setTestResult(null); setMessage("");
    try {
      const r = await api.post(path, payload);
      setTestResult({ label, data: r.data });
    } catch (e) {
      setTestResult({ label, error: formatErr(e) });
    } finally { setTestLoading(""); }
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading client requirements…</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-5 md:p-7">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <button onClick={() => navigate("/ceo-command-center")} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"><ArrowLeft size={14}/> CEO Command Center</button>
            <h1 className="text-2xl font-semibold text-slate-950">Client Requirements Completion</h1>
            <p className="mt-1 max-w-4xl text-sm text-slate-600">The client wording is preserved. ERP data remains authoritative; supplemental values are used only for inputs the legacy ERP does not store and only after they are marked approved. The six closure areas are RM reverse conversion, finance rate, Sysco packing stock, consumables, Breaded aliases, and capacity/documentation semantics.</p>
          </div>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><Save size={16}/>{saving ? "Saving…" : "Save Business Inputs"}</button>
        </div>

        {message ? <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">{message}</div> : null}

        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Remaining completion requirements</div><div className="mt-1 text-3xl font-semibold text-slate-950">{requirements.length}</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Approved supplemental sections</div><div className="mt-1 text-3xl font-semibold text-emerald-700">{approved}/{SECTIONS.length}</div></div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4"><div className="text-xs uppercase tracking-wide text-slate-500">Rule</div><div className="mt-2 text-sm font-medium text-slate-800">No guessed numbers. ERP + explicitly approved business inputs only.</div></div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4"><h2 className="text-base font-semibold text-slate-900">Exact client requirements</h2></div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">ID</th><th className="px-4 py-3">Client wording</th><th className="px-4 py-3">Implemented at</th><th className="px-4 py-3">Completion source</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {requirements.map((r) => <tr key={r.id} className="align-top"><td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500">{r.id}</td><td className="max-w-3xl whitespace-pre-line px-4 py-3 font-medium text-slate-900">{r.requirement}</td><td className="px-4 py-3 text-slate-700">{r.implementation}</td><td className="px-4 py-3"><span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">{r.completion_type}</span></td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-950">Approved business inputs</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            {SECTIONS.map(([key, label]) => <JsonEditor key={key} title={label} value={inputs?.[key] || {}} onChange={(v) => setInputs((old) => ({ ...old, [key]: v }))}/>) }
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">QA / live completion checks</h2>
          <p className="mt-1 text-sm text-slate-500">Run these against the connected ERP after saving approved inputs.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {TESTS.map((t) => <button key={t[0]} onClick={() => runTest(t)} disabled={!!testLoading} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Play size={14}/>{testLoading === t[0] ? "Running…" : t[0]}</button>)}
          </div>
          {testResult ? <div className="mt-4 rounded-xl border border-slate-200 bg-slate-950 p-4 text-xs text-slate-100"><div className="mb-2 flex items-center gap-2 font-semibold">{testResult.error ? <AlertTriangle size={15} className="text-amber-400"/> : <CheckCircle2 size={15} className="text-emerald-400"/>}{testResult.label}</div><pre className="max-h-[420px] overflow-auto whitespace-pre-wrap">{JSON.stringify(testResult.error || testResult.data, null, 2)}</pre></div> : null}
        </div>
      </div>
    </div>
  );
}
